import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { DateFormat } from "utils/format/DateFormat";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";
import { BalanceRegistrationInjection } from "../../di/BalanceRegistrationInjection";
import { BalanceRegistrationDetailEntity } from "../entities/BalanceRegistrationDetailEntity";
import { ExportPayload } from "../entities/BalanceRegistrationPayload";
import {
  BalanceRegistrationQuery,
  SearchContext,
  SearchFormValues,
} from "../entities/BalanceRegistrationQuery";
import { BalanceRegistrationRequestEntity } from "../entities/BalanceRegistrationRequestEntity";
import { BalanceRegistrationResponseEntity } from "../entities/BalanceRegistrationResponseEntity";
import { IBalanceRegistrationRepository } from "../repositories/IBalanceRegistrationRepository";

@injectable()
export class BalanceRegistrationUseCase {
  private balanceRegistrationRepo: IBalanceRegistrationRepository;

  constructor() {
    this.balanceRegistrationRepo =
      BalanceRegistrationInjection.getBalanceRegistrationRepo();
  }

  private prepareFormValues(
    rawFormValues: Record<string, unknown>
  ): SearchFormValues {
    return {
      status: paramsChecking(rawFormValues?.status, "select") as
        | number
        | string,
      code: paramsChecking(rawFormValues?.code, "input") as string,
      platform: paramsChecking(rawFormValues?.platform, "select") as string,
      type: paramsChecking(rawFormValues?.type, "select") as number | string,
    };
  }

  public shouldUsePrivateTransactionList(): boolean {
    const cachedCapabilities = productCache?.getCache(
      CacheKeyEnum.capabilities
    );
    const hasPrivateTransactionList =
      Array.isArray(cachedCapabilities) &&
      cachedCapabilities.some(
        (item) =>
          (typeof item === "string" ? item : item?.key) ===
          CapabilityEnum.privateTransList
      );

    return hasPrivateTransactionList === true;
  }

  private getDateRange(rawFormValues: Record<string, unknown>): {
    dateFrom: string;
    dateTo: string;
  } {
    const dateFrom =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "from"
      ) ?? "";
    const dateTo =
      DateFormat.dateRendering(
        rawFormValues?.created as Array<Record<string, unknown>>,
        "to"
      ) ?? "";
    return { dateFrom, dateTo };
  }

  async getBalanceRegistrationList(
    params: BalanceRegistrationRequestEntity
  ): Promise<ListBaseApiResponseModel<BalanceRegistrationResponseEntity>> {
    return await this.balanceRegistrationRepo.getList(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }

  async getBalanceRegistrationListFromSearch(
    rawFormValues: Record<string, unknown>,
    context: SearchContext
  ): Promise<{
    list: ListBaseApiResponseModel<BalanceRegistrationResponseEntity>;
    params: BalanceRegistrationRequestEntity;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = BalanceRegistrationQuery.buildSearchQuery(
      formValues,
      context,
      dateFrom,
      dateTo
    );
    const list = await this.balanceRegistrationRepo.getList(
      params,
      this.shouldUsePrivateTransactionList()
    );
    return { list, params };
  }

  async getBalanceRegistrationListWithTableChange(
    prevParams: BalanceRegistrationRequestEntity,
    rawFormValues: Record<string, unknown>,
    page: number,
    limit: number
  ): Promise<{
    list: ListBaseApiResponseModel<BalanceRegistrationResponseEntity>;
    params: BalanceRegistrationRequestEntity;
  }> {
    const formValues = this.prepareFormValues(rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(rawFormValues);
    const params = BalanceRegistrationQuery.buildTableChangeQuery(
      prevParams,
      formValues,
      page,
      limit,
      dateFrom,
      dateTo
    );
    const list = await this.balanceRegistrationRepo.getList(
      params,
      this.shouldUsePrivateTransactionList()
    );
    return { list, params };
  }

  async getBalanceRegistrationListRedo(): Promise<{
    list: ListBaseApiResponseModel<BalanceRegistrationResponseEntity>;
    params: BalanceRegistrationRequestEntity;
  }> {
    const { fromDate, toDate } = DateFormat.getDateRange(0);
    const params = BalanceRegistrationQuery.buildRedoQuery(
      fromDate ?? "",
      toDate ?? ""
    );
    const list = await this.balanceRegistrationRepo.getList(
      params,
      this.shouldUsePrivateTransactionList()
    );
    return { list, params };
  }

  async getBalanceRegistrationDetail(
    params: BalanceRegistrationRequestEntity
  ): Promise<BaseApiResponseModel<BalanceRegistrationDetailEntity>> {
    return await this.balanceRegistrationRepo.getDetail(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }

  async exportBalanceRegistrationReportFromSearch(
    payload: ExportPayload
  ): Promise<BaseApiResponseModel<Blob>> {
    const formValues = this.prepareFormValues(payload.rawFormValues);
    const { dateFrom, dateTo } = this.getDateRange(payload.rawFormValues);
    const params = BalanceRegistrationQuery.buildExportQuery(
      formValues,
      dateFrom,
      dateTo
    );
    return await this.balanceRegistrationRepo.exportReport(
      params,
      this.shouldUsePrivateTransactionList()
    );
  }
}
